// LLM197 — In-app notifications bell + drawer.
// Polls unread count on mount, on visibilitychange, and every 30s while
// the tab is foreground. Full list fetches lazily when the drawer opens.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  notificationsList,
  notificationsUnreadCount,
  notificationMarkRead,
  notificationsMarkAllRead,
} from "../services/api";

const POLL_MS = 30_000;

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function NotificationsBell() {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const wrapperRef = useRef(null);

  const refreshUnread = useCallback(async () => {
    try {
      const { unread_count } = await notificationsUnreadCount();
      setUnread(Number(unread_count || 0));
    } catch {
      // Silent: bell just won't update this tick
    }
  }, []);

  const refreshItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationsList({ limit: 50 });
      setItems(data.items || []);
      setUnread(Number(data.unread_count || 0));
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll unread count
  useEffect(() => {
    refreshUnread();
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshUnread();
    };
    document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") refreshUnread();
    }, POLL_MS);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, [refreshUnread]);

  // When drawer opens, fetch fresh list
  useEffect(() => {
    if (open) refreshItems();
  }, [open, refreshItems]);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const handleClick = async (n) => {
    try {
      if (!n.read_at) await notificationMarkRead(n.id);
    } catch {
      // Silent
    }
    setOpen(false);
    if (n.action_url) {
      if (/^https?:/i.test(n.action_url)) {
        window.open(n.action_url, "_blank");
      } else {
        navigate(n.action_url);
      }
    }
    refreshUnread();
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsMarkAllRead();
    } catch {
      // Silent
    }
    await refreshItems();
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={unread ? `${unread} unread notifications` : "Notifications"}
        aria-label="Notifications"
        style={{
          position: "relative",
          background: "rgba(255,255,255,0.12)",
          border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff",
          width: 38,
          height: 38,
          borderRadius: 10,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 0,
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 18,
              height: 18,
              padding: "0 5px",
              borderRadius: 9,
              background: "#ef4444",
              color: "#fff",
              fontSize: 10,
              fontWeight: 800,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              boxShadow: "0 0 0 2px rgba(0,0,0,0.35)",
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 380,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: 520,
            background: "#fff",
            color: "#111827",
            borderRadius: 14,
            boxShadow: "0 16px 44px rgba(15,23,42,0.25)",
            border: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: "column",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              padding: "12px 14px",
              borderBottom: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div style={{ fontWeight: 900, fontSize: 14 }}>Notifications</div>
            {items.some((i) => !i.read_at) && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#2563eb",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                Mark all read
              </button>
            )}
          </div>
          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading ? (
              <div style={{ padding: 18, color: "#6b7280", fontSize: 13, textAlign: "center" }}>
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: 24, color: "#9ca3af", fontSize: 13, textAlign: "center" }}>
                No notifications yet.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleClick(n)}
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "12px 14px",
                    borderBottom: "1px solid #f3f4f6",
                    background: n.read_at ? "#fff" : "#f0f9ff",
                    border: "none",
                    borderLeft: n.read_at ? "none" : "3px solid #2563eb",
                    cursor: n.action_url ? "pointer" : "default",
                  }}
                >
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#111827" }}>{n.title}</div>
                  {n.body_md && (
                    <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{n.body_md}</div>
                  )}
                  <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                    {relativeTime(n.created_at)}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
